<template>
  <!-- Столбики по дням недели: зона нормы, линия лимита или цели, неполные
       дни штрихуются — их цифры не входят в средние (lib/weeklyReport.js) -->
  <div class="days-chart" :class="{ dark }">
    <svg ref="chart" :viewBox="`0 0 ${W} ${H}`" class="w-full h-auto block" role="img" :aria-label="ariaLabel">
      <defs>
        <pattern v-for="pattern in patterns" :key="pattern.id" :id="pattern.id" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="var(--cream)" />
          <rect width="6" height="6" :fill="pattern.color" :fill-opacity="pattern.opacity" />
          <line x1="0" y1="0" x2="0" y2="6" :stroke="pattern.color" stroke-opacity=".55" stroke-width="1.2" />
        </pattern>
      </defs>
      <rect v-if="band" :x="L" :width="plotW" :y="y(band[1])" :height="Math.max(2, y(band[0]) - y(band[1]))"
            :fill="COLORS.band" rx="4" />
      <line v-if="band" :x1="L" :x2="L + plotW" :y1="y(band[1])" :y2="y(band[1])" :stroke="COLORS.bandLine" stroke-width="1" />
      <line v-if="band" :x1="L" :x2="L + plotW" :y1="y(band[0])" :y2="y(band[0])" :stroke="COLORS.bandLine" stroke-width="1" />
      <template v-for="(d, i) in bars" :key="d.date">
        <rect v-if="totalField && d.total > 0" class="total-bar" :x="d.x + bw * .28" :y="y(d.total)" :width="bw" :height="Math.max(2, y(0)-y(d.total))" rx="5" :fill="d.complete ? totalColor : `url(#${totalHatchId})`" />
        <rect v-if="d.v > 0" :x="d.x" :y="y(d.v)" :width="totalField ? bw*.75 : bw" :height="Math.max(2, y(0) - y(d.v))" rx="5"
              :fill="d.complete ? color : `url(#${hatchId})`" />
      </template>
      <template v-if="limit">
        <line :x1="L" :x2="L + plotW" :y1="y(limit)" :y2="y(limit)" :stroke="solidLimit ? 'var(--error)' : COLORS.limit" stroke-width="1.5" :stroke-dasharray="solidLimit ? null : '5 4'" />
      </template>
      <template v-if="target">
        <line :x1="L" :x2="L + plotW" :y1="y(target)" :y2="y(target)" stroke="var(--ink)" stroke-opacity="0.55" stroke-width="1.2" />
      </template>
      <line :x1="L" :x2="L + plotW" :y1="y(0)" :y2="y(0)" stroke="var(--ink)" stroke-opacity="0.15" />
      <!-- Подписи поверх ориентиров: линия не перечёркивает цифры. -->
      <template v-for="d in bars" :key="'labels-'+d.date">
        <text v-if="totalField && d.total > 0" class="lbl-v total-label" :style="{fontSize:labelSize}" :x="d.x + bw*.78" :y="y(d.total)-5" text-anchor="middle">{{ num(d.total,decimals) }}</text>
        <text :x="d.x + bw * (totalField ? .375 : .5)" :y="totalField && d.v > 0 && Math.abs(y(d.v)-y(d.total)) < 20 ? y(d.v)+16 : (d.v > 0 ? y(d.v)-5 : y(0)-5)" text-anchor="middle"
              :style="{fontSize:labelSize}" :class="['lbl-v', { muted: !d.complete }]">{{ d.v > 0 ? num(d.v, decimals) : '—' }}</text>
        <text :x="d.x + bw / 2" :y="H - 4" text-anchor="middle" class="lbl-d" :style="{fontSize:labelSize}">{{ d.weekday }}</text>
      </template>
    </svg>
    <div v-if="!hideLegend" class="legend" :class="{vertical:legendVertical}">
      <span><i :style="{ background: color }" />{{ lower(barLabel) }}</span>
      <span v-if="totalField"><i :style="{background:totalColor}" />{{ lower(totalLabel) }}</span>
      <span v-if="band"><i :style="{ background: COLORS.band, border: `1px solid ${COLORS.bandLine}` }" />{{ lower(bandLabel || `норма ${num(band[0])}–${num(band[1])} ${unit}`) }}</span>
      <span v-if="target"><svg width="24" height="12" viewBox="0 0 24 12" aria-hidden="true"><path d="M0 6h24" stroke="var(--ink)" stroke-opacity=".55" stroke-width="1.2"/></svg>{{ lower(targetLabel || `цель ${num(target)} ${unit}`) }}</span>
      <span v-if="limit"><svg width="24" height="12" viewBox="0 0 24 12" aria-hidden="true"><path d="M0 6h24" :stroke="solidLimit ? 'var(--error)' : COLORS.limit" stroke-width="1.5" :stroke-dasharray="solidLimit ? null : '5 4'"/></svg>{{ lower(limitLabel || `лимит ${num(limit)} ${unit}`) }}</span>
      <span v-if="hasIncomplete"><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <defs><pattern :id="legendHatchId" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="var(--cream)" />
          <rect width="6" height="6" :fill="color" :fill-opacity="totalField ? .45 : .25" />
          <line x1="0" y1="0" x2="0" y2="6" :stroke="color" stroke-opacity=".55" stroke-width="1.2" />
        </pattern></defs>
        <rect width="12" height="12" rx="4" :fill="`url(#${legendHatchId})`"/>
      </svg>день заполнен не полностью</span>
    </div>
  </div>
</template>

<script setup>
import { computed, useId } from 'vue'
import { useChartLabelSize } from '../../utils/useChartLabelSize.js'
const { chart, labelSize } = useChartLabelSize(360)
import { COLORS, num } from '../../utils/weekReport.js'

const props = defineProps({
  days: { type: Array, required: true },
  field: { type: String, required: true },
  color: { type: String, default: COLORS.kcal },
  band: { type: Array, default: null },
  limit: { type: Number, default: null },
  target: { type: Number, default: null },
  decimals: { type: Number, default: 0 },
  unit: { type: String, default: '' },
  barLabel: { type: String, default: 'за день' },
  bandLabel: { type: String, default: '' },
  limitLabel: { type: String, default: '' },
  targetLabel: { type: String, default: '' },
  totalColor: {type:String,default:COLORS.fat},
  totalLabel: {type:String,default:'все жиры'},
  totalField: {type:String,default:''},
  solidLimit: Boolean,
  dark: Boolean,
  hideLegend: Boolean,
  legendVertical: Boolean,
  ariaLabel: { type: String, default: 'График по дням недели' },
})

const lower = text => String(text).toLocaleLowerCase('ru')
const hatchId = 'report-hatch-' + useId()
const totalHatchId = hatchId + '-total'
const legendHatchId = hatchId + '-legend'
const patterns = computed(() => [
  { id: hatchId, color: props.color, opacity: props.totalField ? .45 : .25 },
  { id: totalHatchId, color: props.totalColor, opacity: .2 },
])

const W = 360, H = 172, L = 6, R = 6, T = 20, B = 22
const plotW = W - L - R
const plotH = H - T - B
const slot = plotW / 7
const bw = slot * 0.58

const maxV = computed(() => {
  const vals = props.days.flatMap(d => [Number(d[props.field]) || 0, Number(d[props.totalField]) || 0])
  const m = Math.max(...vals, props.band?.[1] || 0, props.limit || 0, props.target || 0)
  return m > 0 ? m * 1.12 : 1
})
const y = (v) => T + (1 - Math.max(0, v) / maxV.value) * plotH

const bars = computed(() => props.days.map((d, i) => ({
  date: d.date, weekday: d.weekday, complete: !!d.complete,
  v: Number(d[props.field]) || 0,
  total: props.totalField ? Number(d[props.totalField]) || 0 : 0,
  x: L + slot * i + (slot - bw) / 2,
})))
const hasIncomplete = computed(() => bars.value.some(b => !b.complete && b.v > 0))
</script>

<style scoped>
.lbl-v { font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; fill: var(--ink); }
.lbl-v.muted { fill: var(--ink-60); font-weight: 400; }
.lbl-d { font-family: Inter, sans-serif; font-size: var(--fs-16); fill: var(--ink-70); }
.legend { display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 6px; font-size: var(--fs-12); color: var(--ink-70); }
.legend span { display: inline-flex; align-items: center; gap: 5px; }
.legend i { display: inline-block; flex: none; width: 12px; height: 12px; border-radius: 4px; }
.legend i.ln { height: 0; border-top: 1.5px solid var(--ink-60); border-radius: 0; }
.legend i.ln.dash { border-top: 1.5px dashed #B98A5E; }
.legend.vertical { flex-direction: column; gap: 8px; font-size: var(--fs-13); }
.dark :deep(text) { fill: var(--cream); paint-order: stroke fill; stroke: rgb(var(--ink-rgb) / .5); stroke-width: 2px; stroke-linejoin: round; filter: drop-shadow(0 1px 2px rgb(var(--ink-rgb) / .8)); }
.dark .lbl-v.muted { fill: var(--cream); }
</style>
